import { useCallback, useRef, useState } from "react";
import type { ReactNode, Ref } from "react";
import {
  Button as AriaButton,
  Link as AriaLink,
  RouterProvider as AriaRouterProvider,
} from "react-aria-components";
import { useNavigate } from "react-router";

import callCenterIcon from "@/assets/figma/home/call-center.svg";
import headerDividerPrimary from "@/assets/figma/home/header-divider-primary.svg";
import headerDividerSecondary from "@/assets/figma/home/header-divider-secondary.svg";
import userIcon from "@/assets/figma/home/user.svg";
import verticalDivider from "@/assets/figma/home/vertical-divider.svg";
import backIcon from "@/assets/figma/login/back.svg";
import { cn } from "@/shared/lib/cn";
import { MerryCustomerServicePopover } from "@/shared/ui/MerryCustomerServicePopover";
import {
  MerryBrandLockup,
  MerryFunctionButton,
} from "@/shared/ui/MerryFunctionButton";
import { getMerryFunctionButtonFigmaNodeId } from "@/shared/ui/merryTopNavigationVariants";
import {
  getMerryTopNavigationFigmaNodeId,
  type MerryTopNavigationSessionType,
} from "@/shared/ui/merryTopNavigationVariants";
import { useMerryButtonInteractionState } from "@/shared/ui/useMerryButtonInteractionState";

export interface MerryTopNavigationGuestSession {
  loginLabel?: string;
  loginTo?: string;
  onLogin?: () => void;
  type: "guest";
}

export interface MerryTopNavigationAuthenticatedSession {
  account: {
    accessibleLabel: string;
    avatarSource: string;
    label: string;
    onPress?: () => void;
    statusRingSource?: string;
    triggerRef?: Ref<HTMLButtonElement>;
    value: string;
  };
  store?: {
    accessibleLabel: string;
    avatarSources: readonly [string, string, string];
    countryCode?: string;
    label: string;
    onPress?: () => void;
    platformBadge?: string;
    provider?: string;
    triggerRef?: Ref<HTMLButtonElement>;
    value: string;
  };
  type: "authenticated";
}

export type MerryTopNavigationSession =
  | MerryTopNavigationGuestSession
  | MerryTopNavigationAuthenticatedSession;

interface MerryTopNavigationProps {
  agencyName?: string;
  backLabel?: string;
  backTo?: string;
  beforeHomeNavigate?: (navigate: () => void) => void;
  contextTitle?: string;
  figmaNodeId?: string;
  homeTo?: string;
  onBack?: () => void;
  onOpenCustomerService?: () => void;
  session?: MerryTopNavigationSession;
  statusTestIdPrefix?: string;
}

const defaultGuestSession: MerryTopNavigationGuestSession = {
  loginLabel: "登录",
  loginTo: "/login",
  type: "guest",
};

export function MerryTopNavigation({
  agencyName,
  backLabel = "返回首页",
  backTo = "/",
  beforeHomeNavigate,
  contextTitle,
  figmaNodeId,
  homeTo = "/",
  onBack,
  onOpenCustomerService,
  session = defaultGuestSession,
  statusTestIdPrefix = "top-navigation",
}: MerryTopNavigationProps) {
  const navigate = useNavigate();
  const customerServiceTriggerRef = useRef<HTMLButtonElement>(null);
  const customerServiceOpenRef = useRef(false);
  const [isCustomerServiceOpen, setIsCustomerServiceOpen] = useState(false);
  const hasContext = Boolean(contextTitle);
  const sessionType: MerryTopNavigationSessionType = session.type;
  const updateCustomerServiceOpen = useCallback(
    (nextOpen: boolean) => {
      if (customerServiceOpenRef.current === nextOpen) return;

      customerServiceOpenRef.current = nextOpen;
      setIsCustomerServiceOpen(nextOpen);
      if (nextOpen) onOpenCustomerService?.();
    },
    [onOpenCustomerService],
  );

  return (
    <AriaRouterProvider
      navigate={(href) => {
        const destination = String(href);
        if (destination === homeTo) {
          const goHome = () => navigate(destination, { state: null });
          if (beforeHomeNavigate) beforeHomeNavigate(goHome);
          else goHome();
        } else navigate(destination);
      }}
    >
      <>
        <header
          className="merry-top-navigation"
          data-figma-component-id="349:79988"
          data-figma-node-id={
            figmaNodeId ??
            getMerryTopNavigationFigmaNodeId(hasContext, sessionType)
          }
          data-figma-variant-node-id={getMerryTopNavigationFigmaNodeId(
            hasContext,
            sessionType,
          )}
          data-navigation-context={hasContext ? "context" : "default"}
          data-navigation-session={sessionType}
        >
        <NavigationBottomDivider />

        <NavigationLink
          accessibleLabel={agencyName?.trim() ? `Merry 与 ${agencyName.trim()} 首页` : "Merry 首页"}
          className="merry-top-navigation__brand"
          href={homeTo}
          nodeIds={{
            default: getMerryFunctionButtonFigmaNodeId("logo", "default"),
            hovered: getMerryFunctionButtonFigmaNodeId("logo", "hovered"),
            pressed: getMerryFunctionButtonFigmaNodeId("logo", "pressed"),
          }}
        >
          <MerryBrandLockup agencyName={agencyName} />
        </NavigationLink>

        {contextTitle ? (
          <div className="merry-top-navigation__context">
            {onBack ? (
              <NavigationButton
                accessibleLabel={backLabel}
                className="merry-top-navigation__back"
                nodeId={
                  session.type === "authenticated"
                    ? "351:58803"
                    : "351:58676"
                }
                onPress={onBack}
              >
                <NavigationBackIcon />
              </NavigationButton>
            ) : (
              <NavigationLink
                accessibleLabel={backLabel}
                className="merry-top-navigation__back"
                href={backTo}
                nodeIds={{
                  default:
                    session.type === "authenticated"
                      ? "351:58803"
                      : "351:58676",
                }}
              >
                <NavigationBackIcon />
              </NavigationLink>
            )}
            <span
              className="merry-top-navigation__context-title"
              data-figma-node-id={
                session.type === "authenticated" ? "351:58804" : "351:58717"
              }
            >
              {contextTitle}
            </span>
          </div>
        ) : null}

        <div className="merry-top-navigation__actions">
          <NavigationButton
            accessibleLabel="联系客服"
            className="merry-top-navigation__basis-button merry-top-navigation__support"
            hasPopup
            isExpanded={isCustomerServiceOpen}
            nodeId="303:3388"
            onPress={() => updateCustomerServiceOpen(!isCustomerServiceOpen)}
            triggerRef={customerServiceTriggerRef}
          >
            <span aria-hidden="true" className="merry-top-navigation__basis-icon">
              <img alt="" src={callCenterIcon} />
            </span>
          </NavigationButton>

          <span
            aria-hidden="true"
            className="merry-top-navigation__vertical-divider"
          >
            <img alt="" src={verticalDivider} />
          </span>

          {session.type === "authenticated" ? (
            <>
              {session.store ? (
                <MerryFunctionButton
                  aria-label={session.store.accessibleLabel}
                  avatarSources={session.store.avatarSources}
                  className="merry-top-navigation__store-status"
                  countryCode={session.store.countryCode}
                  data-testid={`${statusTestIdPrefix}-store-status`}
                  label={session.store.label}
                  onPress={session.store.onPress}
                  platformBadge={session.store.platformBadge}
                  provider={session.store.provider}
                  triggerRef={session.store.triggerRef}
                  value={session.store.value}
                  variant="store"
                />
              ) : null}
              <MerryFunctionButton
                aria-label={session.account.accessibleLabel}
                avatarSource={session.account.avatarSource}
                className="merry-top-navigation__account-status"
                data-testid={`${statusTestIdPrefix}-account-status`}
                label={session.account.label}
                onPress={session.account.onPress}
                statusRingSource={session.account.statusRingSource}
                triggerRef={session.account.triggerRef}
                value={session.account.value}
                variant="account"
              />
            </>
          ) : (
            <NavigationLogin session={session} hasContext={hasContext} />
          )}
        </div>
        </header>

        <MerryCustomerServicePopover
          isOpen={isCustomerServiceOpen}
          onOpenChange={updateCustomerServiceOpen}
          triggerRef={customerServiceTriggerRef}
        />
      </>
    </AriaRouterProvider>
  );
}

function NavigationBackIcon() {
  return (
    <span aria-hidden="true" className="merry-top-navigation__back-icon">
      <img alt="" src={backIcon} />
    </span>
  );
}

function NavigationBottomDivider() {
  return (
    <span
      aria-hidden="true"
      className="merry-top-navigation__bottom-divider"
    >
      <img
        alt=""
        className="merry-home-navigation__divider merry-home-navigation__divider--dark"
        src={headerDividerPrimary}
      />
      <img
        alt=""
        className="merry-home-navigation__divider merry-home-navigation__divider--light"
        src={headerDividerSecondary}
      />
    </span>
  );
}

function NavigationLogin({
  hasContext,
  session,
}: {
  hasContext: boolean;
  session: MerryTopNavigationGuestSession;
}) {
  const content = (
    <>
      <span aria-hidden="true" className="merry-top-navigation__basis-icon">
        <img alt="" src={userIcon} />
      </span>
      <span className="merry-top-navigation__login-label">
        {session.loginLabel ?? "登录"}
      </span>
    </>
  );
  const nodeId = hasContext ? "351:58814" : "349:80045";

  if (session.onLogin) {
    return (
      <NavigationButton
        accessibleLabel={session.loginLabel ?? "登录"}
        className="merry-top-navigation__basis-button merry-top-navigation__login"
        nodeId={nodeId}
        onPress={session.onLogin}
      >
        {content}
      </NavigationButton>
    );
  }

  return (
    <NavigationLink
      accessibleLabel={session.loginLabel ?? "登录"}
      className="merry-top-navigation__basis-button merry-top-navigation__login"
      href={session.loginTo ?? "/login"}
      nodeIds={{ default: nodeId }}
    >
      {content}
    </NavigationLink>
  );
}

interface NavigationNodeIds {
  default: string;
  hovered?: string;
  pressed?: string;
}

function NavigationLink({
  accessibleLabel,
  children,
  className,
  href,
  nodeIds,
}: {
  accessibleLabel: string;
  children: ReactNode;
  className?: string;
  href: string;
  nodeIds: NavigationNodeIds;
}) {
  const interaction = useMerryButtonInteractionState();

  return (
    <AriaLink
      aria-label={accessibleLabel}
      className={({ isFocusVisible }) =>
        cn(
          "merry-top-navigation__interactive",
          isFocusVisible && "merry-top-navigation__interactive--focus-visible",
          className,
        )
      }
      data-navigation-interaction={interaction.interactionState}
      data-figma-node-id={
        nodeIds[interaction.interactionState] ?? nodeIds.default
      }
      href={href}
      onHoverChange={interaction.onHoverChange}
      onPressChange={interaction.onPressChange}
    >
      {children}
    </AriaLink>
  );
}

function NavigationButton({
  accessibleLabel,
  children,
  className,
  hasPopup = false,
  isExpanded,
  nodeId,
  onPress,
  triggerRef,
}: {
  accessibleLabel: string;
  children: ReactNode;
  className?: string;
  hasPopup?: boolean;
  isExpanded?: boolean;
  nodeId: string;
  onPress?: () => void;
  triggerRef?: Ref<HTMLButtonElement>;
}) {
  const interaction = useMerryButtonInteractionState();

  return (
    <AriaButton
      aria-expanded={hasPopup ? isExpanded : undefined}
      aria-haspopup={hasPopup ? "dialog" : undefined}
      aria-label={accessibleLabel}
      className={({ isFocusVisible }) =>
        cn(
          "merry-top-navigation__interactive",
          isFocusVisible && "merry-top-navigation__interactive--focus-visible",
          className,
        )
      }
      data-navigation-interaction={interaction.interactionState}
      data-figma-node-id={nodeId}
      onHoverChange={interaction.onHoverChange}
      onPress={onPress}
      onPressChange={interaction.onPressChange}
      ref={triggerRef}
      type="button"
    >
      {children}
    </AriaButton>
  );
}
